export interface BasePerson {
  firstName: string;
  lastName: string;
  grade: string;
  gender: string;
  school: string;
  timesAvailable: string[];
}

export interface MentorFormData extends BasePerson {
  email: string;
  phone: string;
  smsOptIn: boolean;
}

export interface ScholarFormData extends BasePerson {
  guardianFirstName: string;
  guardianLastName: string;
  guardianEmail: string;
  guardianPhone: string;
  smsOptIn: boolean;
}

export const MENTOR_GRADES = ["9th", "10th", "11th", "12th"] as const;
export const SCHOLAR_GRADES = ["4th", "5th", "6th", "7th", "8th"] as const;

export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non-binary", label: "Non-binary" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
] as const;

export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export type Day = (typeof DAYS)[number];

export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validatePhone(phone: string): boolean {
  // Allow various phone formats
  return /^[\d\s\-+()]{10,}$/.test(phone.replace(/\s/g, ""));
}

export function validateMentorForm(data: Partial<MentorFormData>): string[] {
  const errors: string[] = [];

  if (!data.firstName?.trim()) errors.push("First name is required");
  if (!data.lastName?.trim()) errors.push("Last name is required");
  if (!data.email?.trim()) errors.push("Email is required");
  else if (!validateEmail(data.email)) errors.push("Please enter a valid email address");
  if (!data.phone?.trim()) errors.push("Phone number is required");
  else if (!validatePhone(data.phone)) errors.push("Please enter a valid phone number");
  if (!data.grade) errors.push("Grade is required");
  if (!data.gender) errors.push("Gender is required");
  if (!data.school?.trim()) errors.push("School is required");
  if (!data.timesAvailable?.length) errors.push("Please select at least one available time");
  if (!data.smsOptIn) errors.push("You must agree to receive text messages to sign up");

  return errors;
}

export function validateScholarForm(data: Partial<ScholarFormData>): string[] {
  const errors: string[] = [];

  if (!data.firstName?.trim()) errors.push("First name is required");
  if (!data.lastName?.trim()) errors.push("Last name is required");
  if (!data.guardianFirstName?.trim()) errors.push("Guardian first name is required");
  if (!data.guardianLastName?.trim()) errors.push("Guardian last name is required");
  if (!data.guardianEmail?.trim()) errors.push("Guardian email is required");
  else if (!validateEmail(data.guardianEmail)) errors.push("Please enter a valid guardian email address");
  if (!data.guardianPhone?.trim()) errors.push("Guardian phone number is required");
  else if (!validatePhone(data.guardianPhone)) errors.push("Please enter a valid guardian phone number");
  if (!data.grade) errors.push("Grade is required");
  if (!data.gender) errors.push("Gender is required");
  if (!data.school?.trim()) errors.push("School is required");
  if (!data.timesAvailable?.length) errors.push("Please select at least one available time");
  if (!data.smsOptIn) errors.push("You must agree to receive text messages to sign up");

  return errors;
}

export const MENTOR_SCHOOL_OPTIONS = [
  "Olmsted North",
  "Assumption High School",
  "Atherton High School",
  "Ballard High School",
  "Beth Haven Christian School",
  "Breckinridge Metropolitan High School",
  "Buechel Metropolitan High School",
  "Butler Traditional High School",
  "Central High School",
  "Christian Academy of Louisville",
  "Covenant Classical Academy",
  "DeSales High School",
  "Doss High School",
  "MCA duPont Manual High School",
  "Eastern High School",
  "Evangel Christian School",
  "Fairdale High School",
  "MCA Families For Christ Christian Academy",
  "Fern Creek High School",
  "Highlands Latin School",
  "Holy Cross High School",
  "Immaculata Classical Academy",
  "Iroquois High School",
  "MCA J. Graham Brown School",
  "Jefferson County High School",
  "Jeffersontown High School",
  "MCA Kentucky Country Day School",
  "Landmark Christian Academy",
  "Liberty High School",
  "Louisville Adventist Academy",
  "Louisville Classical Academy",
  "Louisville Collegiate School",
  "Louisville Male High School",
  "M I C A H School",
  "Marion C. Moore School",
  "Martha Layne Collins High School",
  "Mercy Academy",
  "Northside Christian School",
  "North Creek High School",
  "North Oldham High School",
  "Nur Islamic School of Louisville",
  "Pathfinder School of Innovation",
  "Phoenix School of Discovery",
  "Pitt Academy",
  "Pleasure Ridge Park High School",
  "MCA Portland Christian School",
  "Presentation Academy",
  "Sacred Heart Academy",
  "Seneca High School",
  "MCA Southern High School",
  "South Oldham High School",
  "Francis Parker High School",
  "St. Xavier High School",
  "The Academy @ Shawnee",
  "The Academy for Individual Excellence",
  "Trinity High School",
  "Valley Traditional High School",
  "Valor Traditional Academy",
  "Waggener Traditional High School",
  "Walden School",
  "Western High School",
  "Whitefield Academy",
  "Youth Performing Arts School",
  "Other"
] as const;

export const SCHOLAR_SCHOOL_OPTIONS = [
  "Albert S. Brandeis Elementary School",
  "Atkinson Elementary School",
  "Auburndale Elementary School",
  "Audubon Traditional Elementary School",
  "Bates Elementary School",
  "Blake Elementary School",
  "Bloom Elementary School",
  "Blue Lick Elementary School",
  "Bowen Elementary School",
  "Breckinridge-Franklin Elementary School",
  "Byck Elementary School",
  "Camp Taylor Elementary School",
  "Cane Run Elementary School",
  "Carter Traditional School",
  "Chancey Elementary School",
  "Chenoweth Elementary School",
  "Cochran Elementary School",
  "Coleridge-Taylor Montessori Elementary School",
  "Coral Ridge Elementary School",
  "Crums Lane Elementary School",
  "Dixie Elementary School",
  "Dunn Elementary School",
  "Eisenhower Elementary School",
  "Engelhard Elementary School",
  "Fairdale Elementary School",
  "Families For Christ Christian Academy",
  "Farmer Elementary School",
  "Fern Creek Elementary School",
  "Field Elementary School",
  "Foster Academy",
  "Frayser Elementary School",
  "Gilmore Lane Elementary School",
  "Goldsmith Elementary School",
  "Greathouse/Shryock Traditional Elementary School",
  "Greenwood Elementary School",
  "Gutermuth Elementary School",
  "Hartstern Elementary School",
  "Hawthorne Elementary School",
  "Hazelwood Elementary School",
  "Highlands Latin School",
  "Hite Elementary School",
  "Immaculata Classical Academy",
  "Indian Trail Elementary School",
  "MCA J. Graham Brown School",
  "Jacob Elementary School",
  "Jeffersontown Elementary School",
  "Johnsontown Road Elementary School",
  "Kennedy Montessori School",
  "Kentucky Country Day School",
  "Kenwood Elementary School",
  "Kerrick Elementary School",
  "King Elementary School",
  "Klondike Lane Elementary School",
  "Landmark Christian Academy",
  "Laukhuf Elementary School",
  "Layne Elementary School",
  "Lincoln Elementary School",
  "Louisville Adventist Academy",
  "Louisville Classical Academy",
  "Louisville Collegiate School",
  "Lowe Elementary School",
  "Luhr Elementary School",
  "M I C A H School",
  "Maupin Elementary School",
  "McFerran Preparatory Academy",
  "Medora Elementary School",
  "Mighty Oak Academy",
  "Middletown Elementary School",
  "Mill Creek Elementary School",
  "Minors Lane Elementary School",
  "Northside Christian School",
  "Norton Elementary School",
  "Nur Islamic School of Louisville",
  "Okolona Elementary School",
  "Pathfinder School of Innovation",
  "Pitt Academy",
  "Portland Christian School",
  "Portland Elementary School",
  "Price Elementary School",
  "Rangeland Elementary School",
  "Roosevelt-Perry Elementary School",
  "Rutherford Elementary School",
  "Sanders Elementary School",
  "Sayers Classical Academy",
  "Schaffner Traditional School",
  "Semple Elementary School",
  "Shacklette Elementary School",
  "Shelby Traditional Academy",
  "Slaughter Elementary School",
  "Smyrna Elementary School",
  "St. Francis School",
  "St. Matthews Elementary School",
  "Stonestreet Elementary School",
  "Stopher Elementary School",
  "The Academy for Individual Excellence",
  "Trunnell Elementary School",
  "Tully Elementary School",
  "Valor Traditional Academy",
  "Walden School",
  "Watson Lane Elementary School",
  "Watterson Elementary School",
  "Wellington Elementary School",
  "Wheatley Elementary School",
  "Wheeler Elementary School",
  "Whitefield Academy",
  "Wilder Elementary School",
  "Wilkerson Elementary School",
  "Wilt Elementary School",
  "Young Elementary School",
  "Zachary Taylor Elementary School",
  "Carrithers Middle School",
  "Farnsley Middle School",
  "Highland Middle School",
  "Moore Middle School",
  "Newburg Middle School",
  "Noe Middle School",
  "Thomas Jefferson Middle School",
  "Shawnee Middle School",
  "Western Middle School"
] as const;

